import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getApiUrl } from '../config';
import {
  GlassInfoCard,
  GlassButton,
  GlassIconButton,
  GlassChip,
  GlassDialog,
  GlassTextField,
  GlassCard,
} from './ui/GlassComponents';

const BankOfferingsManager = ({ bankId, bankName, sessionData, isOfficer }) => {
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedOffering, setSelectedOffering] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    interestRateBasisPoints: 0,
    maintenanceFeeAmount: 0,
    minimumBalance: 0,
    maximumBalance: '',
    overdraftAllowed: false,
    overdraftLimit: 0,
    overdraftFeeAmount: 0,
    isActive: true,
  });

  useEffect(() => {
    if (bankId) {
      fetchOfferings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId]);

  const fetchOfferings = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(getApiUrl(`/banks/${bankId}/offerings`), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch bank offerings');
      }
      
      const data = await response.json();
      setOfferings(data);
    } catch (err) {
      setError('Failed to load bank offerings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      interestRateBasisPoints: 0,
      maintenanceFeeAmount: 0,
      minimumBalance: 0,
      maximumBalance: '',
      overdraftAllowed: false,
      overdraftLimit: 0,
      overdraftFeeAmount: 0,
      isActive: true,
    });
  };

  const handleCreateOffering = async () => {
    if (!formData.name.trim()) {
      setError('Offering name is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        interestRateBasisPoints: parseInt(formData.interestRateBasisPoints) || 0,
        maintenanceFeeAmount: parseInt(formData.maintenanceFeeAmount) || 0,
        minimumBalance: parseInt(formData.minimumBalance) || 0,
        maximumBalance: formData.maximumBalance ? parseInt(formData.maximumBalance) : null,
        overdraftAllowed: formData.overdraftAllowed,
        overdraftLimit: parseInt(formData.overdraftLimit) || 0,
        overdraftFeeAmount: parseInt(formData.overdraftFeeAmount) || 0,
        createdBy: sessionData.selectedDisplayName,
      };

      const response = await fetch(getApiUrl(`/banks/${bankId}/offerings`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create offering');
      }

      setCreateDialog(false);
      resetForm();
      await fetchOfferings();
      setError('');
      setSuccess('Bank offering created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to create offering: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOffering = async () => {
    if (!formData.name.trim()) {
      setError('Offering name is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        // Only send fields that can be updated: name, description, and isActive
        // Financial terms cannot be modified after creation
        isActive: formData.isActive,
        updatedBy: sessionData.selectedDisplayName,
      };

      const response = await fetch(getApiUrl(`/banks/${bankId}/offerings/${selectedOffering.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update offering');
      }

      setEditDialog(false);
      setSelectedOffering(null);
      resetForm();
      await fetchOfferings();
      setError('');
      setSuccess('Bank offering updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to update offering: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOffering = async () => {
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/banks/${bankId}/offerings/${selectedOffering.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = 'Failed to delete offering';
        
        // Try to parse JSON error response
        try {
          const errorData = await response.json();
          if (errorData.message) {
            // Use the detailed message if available
            errorMessage = errorData.message;
          } else if (errorData.error) {
            // Use the error field if available
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          // If JSON parsing fails, use status-based messages
          if (response.status === 409) {
            errorMessage = 'Cannot delete offering with existing accounts. This offering cannot be deleted because there are bank accounts currently using it. Consider deactivating the offering instead to prevent new accounts while preserving existing ones.';
          } else if (response.status === 404) {
            errorMessage = 'Bank offering not found';
          } else if (response.status === 401) {
            errorMessage = 'Unauthorized to delete this offering';
          }
        }
        
        throw new Error(errorMessage);
      }

      setDeleteDialog(false);
      setSelectedOffering(null);
      await fetchOfferings();
      setError('');
      setSuccess('Bank offering deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (offering) => {
    setSelectedOffering(offering);
    setFormData({
      name: offering.name,
      description: offering.description || '',
      interestRateBasisPoints: offering.interestRateBasisPoints,
      maintenanceFeeAmount: offering.maintenanceFeeAmount,
      minimumBalance: offering.minimumBalance,
      maximumBalance: offering.maximumBalance || '',
      overdraftAllowed: offering.overdraftAllowed,
      overdraftLimit: offering.overdraftLimit,
      overdraftFeeAmount: offering.overdraftFeeAmount,
      isActive: offering.isActive,
    });
    setEditDialog(true);
  };

  const openDeleteDialog = (offering) => {
    setSelectedOffering(offering);
    setDeleteDialog(true);
  };

  const handleToggleActive = async (offering) => {
    setLoading(true);
    try {
      const payload = {
        isActive: !offering.isActive,
        updatedBy: sessionData.selectedDisplayName,
      };

      const response = await fetch(getApiUrl(`/banks/${bankId}/offerings/${offering.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update offering status');
      }

      await fetchOfferings();
      setError('');
      setSuccess(`Offering ${!offering.isActive ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to update offering status: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat().format(amount);
  };

  const formatInterestRate = (basisPoints) => {
    return `${(basisPoints / 100).toFixed(2)}%`;
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 2
      }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>Account Offerings</Typography>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <GlassIconButton onClick={fetchOfferings} disabled={loading}>
            <RefreshIcon />
          </GlassIconButton>
          {isOfficer && (
            <GlassButton
              variant="glass"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialog(true)}
              sx={{ flex: { xs: 1, sm: '0 0 auto' } }}
            >
              Create Offering
            </GlassButton>
          )}
        </Box>
      </Box>

      {error && (
        <GlassInfoCard severity="error" sx={{ mb: 2 }}>
          {error}
        </GlassInfoCard>
      )}

      {success && (
        <GlassInfoCard severity="success" sx={{ mb: 2 }}>
          {success}
        </GlassInfoCard>
      )}

      {loading && !offerings.length ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : offerings.length === 0 ? (
        <GlassInfoCard severity="info">
          No account offerings available. {isOfficer ? 'Create the first offering!' : ''}
        </GlassInfoCard>
      ) : (
        <TableContainer component={GlassCard}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Interest Rate</TableCell>
                <TableCell>Maintenance Fee</TableCell>
                <TableCell>Min Balance</TableCell>
                <TableCell>Max Balance</TableCell>
                <TableCell>Overdraft</TableCell>
                <TableCell>Status</TableCell>
                {isOfficer && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {offerings.map((offering) => (
                <TableRow key={offering.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {offering.name}
                      </Typography>
                      {offering.description && (
                        <Typography variant="caption" color="text.secondary">
                          {offering.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{formatInterestRate(offering.interestRateBasisPoints)}</TableCell>
                  <TableCell>{formatCurrency(offering.maintenanceFeeAmount)}</TableCell>
                  <TableCell>{formatCurrency(offering.minimumBalance)}</TableCell>
                  <TableCell>
                    {offering.maximumBalance ? formatCurrency(offering.maximumBalance) : 'No limit'}
                  </TableCell>
                  <TableCell>
                    {offering.overdraftAllowed ? (
                      <Tooltip title={`Limit: ${formatCurrency(offering.overdraftLimit)}, Fee: ${formatCurrency(offering.overdraftFeeAmount)}`}>
                        <GlassChip variant="success" size="small">Allowed</GlassChip>
                      </Tooltip>
                    ) : (
                      <GlassChip variant="default" size="small">Not allowed</GlassChip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GlassChip
                        variant={offering.isActive ? 'success' : 'default'}
                        size="small"
                      >
                        {offering.isActive ? 'Active' : 'Inactive'}
                      </GlassChip>
                      {isOfficer && (
                        <Tooltip title={offering.isActive ? 'Deactivate offering' : 'Activate offering'}>
                          <GlassIconButton
                            size="small"
                            onClick={() => handleToggleActive(offering)}
                            disabled={loading}
                          >
                            <Switch
                              checked={offering.isActive}
                              size="small"
                              sx={{ pointerEvents: 'none' }}
                            />
                          </GlassIconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  {isOfficer && (
                    <TableCell align="center">
                      <GlassIconButton
                        size="small"
                        onClick={() => openEditDialog(offering)}
                        disabled={loading}
                      >
                        <EditIcon />
                      </GlassIconButton>
                      <GlassIconButton
                        size="small"
                        onClick={() => openDeleteDialog(offering)}
                        disabled={loading}
                        sx={{
                          color: 'rgba(255, 107, 107, 0.9)',
                          border: '1px solid rgba(255, 107, 107, 0.3)',
                          '&:hover': {
                            background: 'rgba(255, 107, 107, 0.15)',
                          }
                        }}
                      >
                        <DeleteIcon />
                      </GlassIconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Offering Dialog */}
      <GlassDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="md"
        fullWidth
        title="Create Account Offering"
        actions={
          <>
            <GlassButton onClick={() => setCreateDialog(false)}>Cancel</GlassButton>
            <GlassButton onClick={handleCreateOffering} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Create'}
            </GlassButton>
          </>
        }
      >
          <GlassTextField
            fullWidth
            label="Offering Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            placeholder="e.g., Basic Savings, Premium Checking"
          />
          
          <GlassTextField
            fullWidth
            label="Description (optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
            placeholder="Brief description of this account type"
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <GlassTextField
              label="Interest Rate (basis points)"
              type="number"
              value={formData.interestRateBasisPoints}
              onChange={(e) => setFormData({ ...formData, interestRateBasisPoints: e.target.value })}
              helperText="100 basis points = 1%"
              inputProps={{ min: 0, max: 10000 }}
            />
            
            <GlassTextField
              label="Monthly Maintenance Fee"
              type="number"
              value={formData.maintenanceFeeAmount}
              onChange={(e) => setFormData({ ...formData, maintenanceFeeAmount: e.target.value })}
              helperText="Fee charged monthly"
              inputProps={{ min: 0 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <GlassTextField
              label="Minimum Balance"
              type="number"
              value={formData.minimumBalance}
              onChange={(e) => setFormData({ ...formData, minimumBalance: e.target.value })}
              helperText="Required minimum balance"
              inputProps={{ min: 0 }}
            />
            
            <GlassTextField
              label="Maximum Balance (optional)"
              type="number"
              value={formData.maximumBalance}
              onChange={(e) => setFormData({ ...formData, maximumBalance: e.target.value })}
              helperText="Leave empty for no limit"
              inputProps={{ min: 0 }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={formData.overdraftAllowed}
                onChange={(e) => setFormData({ ...formData, overdraftAllowed: e.target.checked })}
              />
            }
            label="Allow Overdraft"
          />

          {formData.overdraftAllowed && (
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <GlassTextField
                label="Overdraft Limit"
                type="number"
                value={formData.overdraftLimit}
                onChange={(e) => setFormData({ ...formData, overdraftLimit: e.target.value })}
                helperText="Maximum overdraft amount"
                inputProps={{ min: 0 }}
              />
              
              <GlassTextField
                label="Overdraft Fee"
                type="number"
                value={formData.overdraftFeeAmount}
                onChange={(e) => setFormData({ ...formData, overdraftFeeAmount: e.target.value })}
                helperText="Fee per overdraft occurrence"
                inputProps={{ min: 0 }}
              />
            </Box>
          )}
      </GlassDialog>

      {/* Edit Offering Dialog */}
      <GlassDialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="md"
        fullWidth
        title="Edit Account Offering"
        actions={
          <>
            <GlassButton onClick={() => setEditDialog(false)}>Cancel</GlassButton>
            <GlassButton onClick={handleUpdateOffering} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Update'}
            </GlassButton>
          </>
        }
      >
          <GlassInfoCard severity="info" sx={{ mb: 2 }}>
            Only the name, description, and active status can be modified after creation.
            Financial terms are locked to protect existing account holders.
          </GlassInfoCard>
          
          <GlassTextField
            fullWidth
            label="Offering Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          
          <GlassTextField
            fullWidth
            label="Description (optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />

          <Divider sx={{ my: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  {formData.isActive ? 'Active' : 'Inactive'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formData.isActive 
                    ? 'New accounts can be opened with this offering' 
                    : 'Blocks new account creation (existing accounts unaffected)'}
                </Typography>
              </Box>
            }
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Financial Terms (Read-Only)
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <GlassTextField
              label="Interest Rate (basis points)"
              type="number"
              value={formData.interestRateBasisPoints}
              helperText="100 basis points = 1%"
              inputProps={{ min: 0, max: 10000 }}
              disabled
            />
            
            <GlassTextField
              label="Monthly Maintenance Fee"
              type="number"
              value={formData.maintenanceFeeAmount}
              disabled
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <GlassTextField
              label="Minimum Balance"
              type="number"
              value={formData.minimumBalance}
              disabled
            />
            
            <GlassTextField
              label="Maximum Balance (optional)"
              type="number"
              value={formData.maximumBalance}
              disabled
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.overdraftAllowed}
                disabled
              />
            }
            label="Allow Overdraft"
          />

          {formData.overdraftAllowed && (
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <GlassTextField
                label="Overdraft Limit"
                type="number"
                value={formData.overdraftLimit}
                disabled
              />
              
              <GlassTextField
                label="Overdraft Fee"
                type="number"
                value={formData.overdraftFeeAmount}
                disabled
              />
            </Box>
          )}
      </GlassDialog>

      {/* Delete Offering Dialog */}
      <GlassDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
        title="Delete Account Offering"
        actions={
          <>
            <GlassButton onClick={() => setDeleteDialog(false)}>Cancel</GlassButton>
            <GlassButton
              onClick={handleDeleteOffering}
              disabled={loading}
              sx={{
                background: 'rgba(255, 107, 107, 0.15)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                color: 'rgba(255, 107, 107, 0.9)',
                '&:hover': {
                  background: 'rgba(255, 107, 107, 0.25)',
                }
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Delete'}
            </GlassButton>
          </>
        }
      >
          <GlassInfoCard severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete the offering <strong>{selectedOffering?.name}</strong>?
            This action cannot be undone.
          </GlassInfoCard>
          <GlassInfoCard severity="info" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'rgba(255, 255, 255, 1)' }}>
                Note
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                Offerings cannot be deleted if they have existing accounts.
                If deletion fails, consider <strong>deactivating</strong> the offering instead to prevent new accounts while preserving existing ones.
              </Typography>
            </Box>
          </GlassInfoCard>
          {selectedOffering && (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              This will permanently remove the account offering from {bankName}.
            </Typography>
          )}
      </GlassDialog>
    </Box>
  );
};

export default BankOfferingsManager; 